import { Delete } from "@material-ui/icons";
import styled from "styled-components";

import type { AuthorType, BadgeType } from "./components";
import { AddIcon, Author, BadgeList } from "./components";
import { translatePainLocation } from "utils/translations/translatePainLocation";

interface Props {
  author?: AuthorType;
  badges?: BadgeType[];
  bottomContent?: React.ReactNode;
  caption?: string;
  checked?: boolean;
  icon?: React.ReactNode;
  id?: number;
  image?: React.ReactNode;
  selected?: boolean;
  showAddButton?: boolean;
  tags?: string[];
  title?: string;
  width?: string;
  onClick?: (id: number) => void;
  onDelete?: () => void;
  onMoreInfoClick?: () => void;
}

export const LibraryItem: React.VFC<Props> = ({
  author,
  badges = [],
  bottomContent,
  caption,
  checked,
  icon,
  id,
  image,
  selected,
  showAddButton,
  tags = [],
  title,
  width,
  onClick,
  onDelete,
  onMoreInfoClick,
}) => {
  const translatedTags = tags?.map(tag => translatePainLocation({ painLocation: tag })) as string[];

  return (
    <Container
      onClick={() => {
        if (onClick && id) {
          onClick(id);
        }
      }}
      $selected={selected}
      $width={width}
    >
      <TopRow>
        {image && <Image>{image}</Image>}
        <Column>
          <TitleRow>
            {title && <Title>{title}</Title>} {icon}
          </TitleRow>
          {caption && <Caption>{caption}</Caption>}
          {onMoreInfoClick && (
            <Catcher
              onClick={e => {
                e.stopPropagation();
              }}
            >
              <LinkText onClick={onMoreInfoClick} role="button">
                More info
              </LinkText>
            </Catcher>
          )}
        </Column>

        {onDelete && (
          <ButtonContainer
            onClick={e => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Delete style={{ height: "24px", width: "24px" }} />
          </ButtonContainer>
        )}

        {showAddButton && checked !== undefined && (
          <ButtonContainer>
            <AddIcon checked={checked} />
          </ButtonContainer>
        )}
      </TopRow>

      {translatedTags.length > 0 && (
        <Tags>
          {translatedTags.map(tag => (
            <JointTag key={tag}>{tag}</JointTag>
          ))}
        </Tags>
      )}

      <BottomRow>
        <Author author={author} />
        {badges.length > 0 && <BadgeList badges={badges} />}
      </BottomRow>

      {bottomContent && <BottomRow>{bottomContent}</BottomRow>}
    </Container>
  );
};

const Container = styled.div<{ $selected?: boolean; $width?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 12px;
  background-color: ${({ theme, $selected }) => ($selected ? theme.colors.redesign.b10 : theme.colors.white)};
  border: 1px solid;
  border-color: ${({ theme, $selected }) => ($selected ? theme.colors.redesign.b100 : "rgb(225, 232, 239)")};
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.redesign.b100};
    background-color: ${({ theme }) => theme.colors.redesign.b10};
  }

  ${({ theme }) => theme.aboveBreakpoint} {
    width: ${({ $width }) => $width || "calc(100% / 3 - 32px)"};
  }

  min-width: 240px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const TopRow = styled(Row)`
  gap: 12px;
`;

const BottomRow = styled(Row)`
  align-items: center;
  justify-content: space-between;
`;

const TitleRow = styled(Row)`
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const Image = styled.div`
  height: 88px;

  & > svg {
    height: 60px;
    width: 60px;
  }
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const Caption = styled.span`
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #000;
`;

const Tags = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const JointTag = styled.div`
  font-size: 14px;
  padding: 4px 8px 4px 8px;
  border-radius: 4px;
  display: inline-block;
  background-color: #f2f2f2;
`;

const ButtonContainer = styled.div`
  align-self: flex-start;
  margin-left: auto;

  &:hover {
    svg {
      color: ${({ theme }) => theme.colors.redesign.b100};
    }
  }
`;

const LinkText = styled.div`
  color: #5096e8;
  font-size: 12px;
  font-weight: 600;
`;

const Catcher = styled.div`
  margin-top: 4px;
`;
